import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Warehouse, Truck } from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import StatsSection from '../components/sections/StatsSection';
import { useLanguage } from '../context/LanguageContext';
import { companyInfo } from '../data/mockData';

const About = () => {
  const { t, isRTL } = useLanguage();

  return (
    <div className="min-h-screen">
      <Header />
      <main dir={isRTL ? 'rtl' : 'ltr'}>
        {/* Hero Banner */}
        <section className="pt-32 pb-20 bg-gradient-to-br from-[#e8d4e5] via-[#d9c5d6] to-[#c5a3c0]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="max-w-3xl"
            >
              <p className="text-[#9B4B8C] text-sm font-medium uppercase tracking-wider mb-4">
                {t.about.heroTitle}
              </p>
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                {t.about.heroSubtitle}
              </h1>
              <p className="text-lg text-gray-700 leading-relaxed">
                {t.about.heroDescription}
              </p>
            </motion.div>
          </div>
        </section>

        {/* About Content */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className={`grid grid-cols-1 lg:grid-cols-2 gap-16 items-center ${isRTL ? 'lg:flex-row-reverse' : ''}`}>
              <motion.div
                initial={{ opacity: 0, x: isRTL ? 30 : -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              >
                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                  {t.about.section1Title}
                </h2>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  {t.about.section1Text}
                </p>
                
                <div className="flex flex-wrap gap-4">
                  {companyInfo.certifications.map((cert) => (
                    <div
                      key={cert}
                      className={`flex items-center gap-2 bg-[#9B4B8C]/10 px-4 py-2 rounded-full ${isRTL ? 'flex-row-reverse' : ''}`}
                    >
                      <CheckCircle className="w-5 h-5 text-[#9B4B8C]" />
                      <span className="text-sm font-medium text-gray-700">{cert}</span>
                    </div>
                  ))}
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: isRTL ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="relative"
              >
                <img
                  src="https://customer-assets.emergentagent.com/job_f79f1573-be7d-47b7-99e2-70d1f8759bc8/artifacts/cin984lp_Entrepot%20et%20march%C3%A91.png"
                  alt="Entrepôt Boidy"
                  className="rounded-2xl shadow-xl w-full"
                />
                <div className={`absolute -bottom-8 ${isRTL ? '-right-8' : '-left-8'} bg-[#9B4B8C] text-white p-6 rounded-xl shadow-lg`}>
                  <div className="text-4xl font-bold">6+</div>
                  <div className="text-sm text-white/80">{t.stats[0]?.label}</div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Mission Section */}
        <section className="py-24 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className={`grid grid-cols-1 lg:grid-cols-2 gap-16 items-center`}>
              <motion.div
                initial={{ opacity: 0, x: isRTL ? 30 : -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className={isRTL ? 'order-1' : 'order-2 lg:order-1'}
              >
                <img
                  src="https://customer-assets.emergentagent.com/job_f79f1573-be7d-47b7-99e2-70d1f8759bc8/artifacts/sarxlfer_Entrepot%20et%20march%C3%A92.png"
                  alt="Marché Boidy"
                  className="rounded-2xl shadow-xl w-full"
                />
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: isRTL ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className={isRTL ? 'order-2' : 'order-1 lg:order-2'}
              >
                <p className="text-[#9B4B8C] text-sm font-medium uppercase tracking-wider mb-4">
                  {t.about.missionTitle}
                </p>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                  {t.about.missionSubtitle}
                </h2>
                <p className="text-gray-600 leading-relaxed">
                  {t.about.missionText}
                </p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section id="services" className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <p className="text-[#9B4B8C] text-sm font-medium uppercase tracking-wider mb-4">
                {t.services.title}
              </p>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                {t.services.subtitle}
              </h2>
              <p className="text-gray-600 max-w-3xl mx-auto">
                {t.services.description}
              </p>
            </motion.div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              {t.services.items.map((service, index) => (
                <motion.div
                  key={service.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-gray-50 rounded-2xl p-8 hover:shadow-xl transition-shadow"
                >
                  <div className="w-16 h-16 bg-[#9B4B8C]/10 rounded-xl flex items-center justify-center mb-6">
                    {index === 0 ? (
                      <Warehouse className="w-8 h-8 text-[#9B4B8C]" />
                    ) : (
                      <Truck className="w-8 h-8 text-[#9B4B8C]" />
                    )}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{service.title}</h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">{service.description}</p>
                  <ul className="space-y-3">
                    {service.features.map((feature) => (
                      <li key={feature} className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <CheckCircle className="w-5 h-5 text-[#9B4B8C] flex-shrink-0" />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <StatsSection />
      </main>
      <Footer />
    </div>
  );
};

export default About;
